import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticBattery } from "./realistic";

/**
 * BatteryNode – Battery/UPS visualization.
 * Supports:
 * - Realistic 3D SCADA design with dynamic 5-segment level gauge & charging animation
 * - Glassmorphism design
 * - Classic industrial SVG layout
 */
const BatteryNode = (props) => {
  return (
    <BaseNode {...props}>
      {({ isRunning, isOffline, statusColor, speedScale, designType, color, opacity, liveValue, unit, config, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticBattery
              liveValue={liveValue}
              unit={unit || "%"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              config={config || props.data?.config}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }

        const dotColor =
          statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";
        const showOldDesign = designType === "new_design" || designType === "classic";
        
        const isCharging = props.data?.config?.isCharging ?? true;
        const minV = props.data?.config?.minValue ?? 0;
        const maxV = props.data?.config?.maxValue ?? 100;
        const rawPct = (liveValue !== null && liveValue !== undefined && maxV > minV)
          ? ((Number(liveValue) - minV) / (maxV - minV)) * 100
          : (liveValue !== null && liveValue !== undefined ? Number(liveValue) : 75);
        const level = Math.min(100, Math.max(0, rawPct));
        const fillWidth = (level / 100) * 46;
        
        const isDead = liveValue !== null ? liveValue <= 0 : false;
        
        // Charging animation pulse duration based on speedScale (proportional to min/max configuration)
        const pulseDuration = !isDead && isCharging ? `${Math.max(0.4, 2.0 / (speedScale || 1))}s` : "0s";
        
        // If dead/inactive, levelColor is red. Otherwise, green for >60%, orange for >25%, red for <=25%.
        const levelColor = isDead ? "#ef4444" : (level > 60 ? "#22c55e" : level > 25 ? "#f59e0b" : "#ef4444");

        return (
          <svg
            width="70"
            height="56"
            viewBox="0 0 70 56"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              opacity,
              filter: (isOffline || isDead) ? "grayscale(100%) opacity(0.6)" : "none",
              overflow: "visible",
              transition: "all 0.3s ease",
            }}
          >
            {!showOldDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                <defs>
                  <linearGradient id="batGlassGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
                  </linearGradient>
                  <linearGradient id="batActiveGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={levelColor} stopOpacity="0.8" />
                    <stop offset="100%" stopColor={levelColor} stopOpacity="0.4" />
                  </linearGradient>
                  <filter id="batGlow" x="-25%" y="-25%" width="150%" height="150%">
                    <feGaussianBlur stdDeviation="2" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* Battery outer enclosure */}
                <rect x="5" y="8" width="52" height="28" rx="4" fill="url(#batGlassGrad)" stroke={levelColor} strokeWidth="2.5" />
                {/* Battery terminal (right cap) */}
                <rect x="57" y="16" width="6" height="12" rx="1.5" fill={levelColor} />

                {/* Glow ring around the battery if active */}
                {!isDead && (
                  <rect x="3" y="6" width="56" height="32" rx="6" stroke={levelColor} strokeWidth="1" fill="none" opacity="0.3" filter="url(#batGlow)" />
                )}

                {/* Fill level segment bars (glowing and high visibility) */}
                <g filter={!isDead ? "url(#batGlow)" : "none"}>
                  <rect
                    x="8"
                    y="11"
                    width={fillWidth}
                    height="22"
                    rx="2"
                    fill="url(#batActiveGrad)"
                    style={{ transition: "width 0.5s ease" }}
                  />
                </g>

                {/* Charging lightning bolt animation */}
                {isCharging && !isDead && (
                  <g filter="url(#batGlow)">
                    {/* Pulsing lightning icon */}
                    <path
                      d="M32 12 L27 21 L32 21 L28 32 L38 20 L32 20 Z"
                      fill="#ffffff"
                      opacity="0.9"
                    >
                      <animate attributeName="opacity" values="0.9;0.3;0.9" dur={pulseDuration} repeatCount="indefinite" />
                    </path>
                  </g>
                )}

                {/* Digital readout text */}
                <text x="31" y="25.5" textAnchor="middle" fontSize="10.5" fontFamily="monospace" fontWeight="900" fill="#ffffff">
                  {Math.round(level)}%
                </text>

                {/* Premium status dot - top right */}
                <circle cx="62" cy="8" r="4.5" fill={dotColor} stroke="#ffffff" strokeWidth="1" filter={!isDead ? "url(#batGlow)" : "none"}>
                  {!isDead && (
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.2s" repeatCount="indefinite" />
                  )}
                </circle>
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* Battery body */}
                <rect x="5" y="8" width="52" height="28" rx="4" fill={levelColor} fillOpacity={0.05} stroke={levelColor} strokeWidth="2" />

                {/* Battery terminal (right cap) */}
                <rect x="57" y="16" width="6" height="12" rx="2" fill={levelColor} />

                {/* Fill level */}
                <rect
                  x="8"
                  y="11"
                  width={fillWidth}
                  height="22"
                  rx="2"
                  fill={levelColor}
                  fillOpacity={0.6}
                  style={{ transition: "width 0.5s ease" }}
                />

                {/* Charging pulse */}
                {isCharging && !isDead && (
                  <g style={{ animation: `live-battery-pulse ${pulseDuration} ease-in-out infinite` }}>
                    {/* Lightning bolt */}
                    <path d="M28 14 L24 22 L29 22 L25 30 L34 20 L29 20 L33 14 Z" fill="#ffffff" opacity="0.9" />
                  </g>
                )}

                {/* Level text */}
                <text x="31" y="26" textAnchor="middle" fontSize="10" fontWeight="700" fill={level > 50 && !isDead ? "#fff" : levelColor}>
                  {Math.round(level)}%
                </text>

                {/* Status indicator - top right */}
                <circle cx="62" cy="8" r="3" fill={dotColor} />
              </>
            )}

            {/* Label - clearly below battery body */}
            <text x="35" y="49" textAnchor="middle" fontSize="7" fontWeight="600" fill={dotColor}>
              {isOffline ? "OFFLINE" : isDead ? "DEAD" : (isCharging ? "CHARGING" : "DISCHARGE")}
            </text>
          </svg>
        );
      }}
    </BaseNode>
  );
};

BatteryNode.propTypes = { data: PropTypes.object, selected: PropTypes.bool, id: PropTypes.string };
export default BatteryNode;
